import { Permission, wait } from '@cims-online/shared'
import { CustomRowActionOptions } from 'components/CIMSTable/CIMSTableContext/types/actions/row'
import { PayItemConnection, useConnectionsContext } from '../ConnectionsContext'
import { useCallback } from 'react'
import { simpleSuccessNotification } from 'hooks/notificationHandler'
import { useSyncGoogleSheetPayItemConnection } from '../../../../../hooks/useSyncGoogleSheetPayItemConnection'
import { useKeyedBooleans } from 'hooks/useKeyedBooleans'

export function useCustom(): CustomRowActionOptions<PayItemConnection>[] {
  const [loading, { add, remove }] = useKeyedBooleans()
  const { refetch } = useConnectionsContext()
  const syncGoogleSheet = useSyncGoogleSheetPayItemConnection()
  /**
   * this is highly abstracted on purpose so it's easy to add more sync mutations
   * over time
   */
  const sync = useCallback<
    (options: {
      syncMutation: (connectionId: string) => Promise<unknown>
      hidden: (record: PayItemConnection) => boolean
      name: (record: PayItemConnection) => string | undefined
    }) => CustomRowActionOptions<PayItemConnection>
  >(
    ({ hidden, syncMutation, name }) => ({
      permission: Permission.SyncPayItemConnection,
      text: 'Sync',
      hidden,
      disabled: ({ id, syncing }) =>
        syncing || loading(id)
          ? {
              message:
                'This connection is already syncing. Reload or Come back later.',
            }
          : false,
      onClick: async (record) => {
        const resolved = record.name ?? name(record) ?? 'Connection'
        simpleSuccessNotification(`${resolved} sync successfully requested!`)
        add(record.id)
        syncMutation(record.id).finally(async () => {
          await wait(1000)
          refetch()
          remove(record.id)
        })
      },
    }),
    // don't listen to add/remove
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loading, refetch]
  )
  return [
    sync({
      hidden: ({ googleSheet }) => !googleSheet,
      syncMutation: (connectionId) =>
        syncGoogleSheet({ variables: { input: { connectionId } } }),
      name: ({ googleSheet }) => googleSheet?.title ?? googleSheet?.compositeId,
    }),
  ]
}
